import { dedent } from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemasParams,
  Provider,
  QueryAndOutputs,
  TableSchemasList,
  WorkflowComponentCodeParams,
  WorkflowComponentOutputType,
  WorkflowComponentParamType
} from '../types'
import { addColumns } from '../utils/schemas'
import { FILTER_TABLE_FALSE_NAME, FILTER_TABLE_TRUE_NAME } from './common/filtercomponent'
import { WorkflowComponent } from './common/workflowcomponent'

const geomCol = 'geom'

class OracleGeocode extends WorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.oracle_geocode',
        group: 'Spatial Constructors',
        title: 'Oracle Geocode',
        description: `
        This component geocodes addresses using Oracle's native ELOC_GEOCODE functions.
        It supports both unparsed (single address field) and parsed (separate fields) geocoding.
        `,
        starred: false
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'geocode_mode',
          type: WorkflowComponentParamType.Selection,
          title: 'Geocoding mode',
          options: ['Unparsed Address', 'Parsed Address'],
          default: 'Unparsed Address'
        },
        {
          name: 'address',
          type: WorkflowComponentParamType.Column,
          title: 'Full address column (for unparsed mode)',
          parent: 'source',
          placeholder: 'address',
          dataType: [ColumnDataType.String],
          optional: true
        },
        {
          name: 'street',
          type: WorkflowComponentParamType.Column,
          title: 'Street address column (for parsed mode)',
          parent: 'source',
          placeholder: 'street',
          dataType: [ColumnDataType.String],
          optional: true
        },
        {
          name: 'city',
          type: WorkflowComponentParamType.Column,
          title: 'City column (for parsed mode)',
          parent: 'source',
          placeholder: 'city',
          dataType: [ColumnDataType.String],
          optional: true
        },
        {
          name: 'state',
          type: WorkflowComponentParamType.Column,
          title: 'State/Province column (for parsed mode)',
          parent: 'source',
          placeholder: 'state',
          dataType: [ColumnDataType.String],
          optional: true
        },
        {
          name: 'postal_code',
          type: WorkflowComponentParamType.Column,
          title: 'Postal code column (for parsed mode)',
          parent: 'source',
          placeholder: 'postal_code',
          dataType: [ColumnDataType.String],
          optional: true
        },
        {
          name: 'country',
          type: WorkflowComponentParamType.Column,
          title: 'Country column (for parsed mode)',
          parent: 'source',
          placeholder: 'country',
          dataType: [ColumnDataType.String],
          optional: true
        },
        {
          name: 'output_mode',
          type: WorkflowComponentParamType.Selection,
          title: 'Output mode',
          options: ['Geometry Only', 'Full JSON Response'],
          default: 'Geometry Only'
        }
      ],
      [
        {
          name: FILTER_TABLE_TRUE_NAME,
          title: 'Matched',
          description: 'Table with geocoded rows',
          type: WorkflowComponentOutputType.Table
        },
        {
          name: FILTER_TABLE_FALSE_NAME,
          title: 'Unmatched',
          description: 'Table with rows that could not be geocoded',
          type: WorkflowComponentOutputType.Table
        }
      ]
    )
  }

  supportedProviders(): Provider[] {
    return [Provider.Oracle]
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    if (connection.type !== Provider.Oracle) {
      return null
    }

    const tablename = connection.getOutputTableName(workflow.id, componentName, params, 'tempgeocoding', codeType)
    const tablematch = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      FILTER_TABLE_TRUE_NAME,
      codeType
    )
    const tableunmatch = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      FILTER_TABLE_FALSE_NAME,
      codeType
    )

    const preparedParams = this._prepareParameters(params)
    const mode = preparedParams.geocode_mode as string
    const outputMode = preparedParams.output_mode as string

    let geocodeFunction: string
    let geocodeParams: string

    // Choose the appropriate Oracle geocoding function
    if (outputMode === 'Geometry Only') {
      geocodeFunction = 'SDO_GCDR.ELOC_GEOCODE_AS_GEOM'
    } else {
      geocodeFunction = 'SDO_GCDR.ELOC_GEOCODE'
    }

    // Build the geocoding parameters based on mode
    if (mode === 'Unparsed Address') {
      // Single address string
      const addressCol = preparedParams.address as string
      geocodeParams = `t.${addressCol}`
    } else {
      // Parsed address with separate fields
      const streetCol = preparedParams.street ? `t.${preparedParams.street as string}` : 'NULL'
      const cityCol = preparedParams.city ? `t.${preparedParams.city as string}` : 'NULL'
      const stateCol = preparedParams.state ? `t.${preparedParams.state as string}` : 'NULL'
      const postalCol = preparedParams.postal_code ? `t.${preparedParams.postal_code as string}` : 'NULL'
      const countryCol = preparedParams.country ? `t.${preparedParams.country as string}` : 'NULL'

      geocodeParams = `${streetCol}, ${cityCol}, ${stateCol}, ${postalCol}, ${countryCol}`
    }

    // Create the geocoding query
    let geocodeSelect: string

    if (outputMode === 'Geometry Only') {
      // ELOC_GEOCODE_AS_GEOM returns SDO_GEOMETRY directly
      geocodeSelect = dedent`
        SELECT
          t.*,
          ${geocodeFunction}(${geocodeParams}) AS geom
        FROM ${connection.quoteName(preparedParams.source as string)} t
      `
    } else {
      // For full JSON response, keep both the geometry and metadata
      geocodeSelect = dedent`
        SELECT
          t.*,
          SDO_GCDR.ELOC_GEOCODE_AS_GEOM(${geocodeParams}) AS geom,
          SDO_GCDR.ELOC_GEOCODE(${geocodeParams}) AS carto_geocode_metadata
        FROM ${connection.quoteName(preparedParams.source as string)} t
      `
    }

    const createQuery = connection.getCreateTempTableQuery(tablename, geocodeSelect, codeType, null)

    const matchQuery = connection.getCreateTempTableQuery(
      tablematch,
      `SELECT * FROM ${connection.quoteName(tablename)} WHERE geom IS NOT NULL`,
      codeType,
      null
    )

    const unmatchQuery = connection.getCreateTempTableQuery(
      tableunmatch,
      `SELECT * FROM ${connection.quoteName(tablename)} WHERE geom IS NULL`,
      codeType,
      null
    )

    const query = dedent`
      ${createQuery}
      ${matchQuery}
      ${unmatchQuery}
    `

    const outputs: Record<string, string> = {}
    outputs[FILTER_TABLE_TRUE_NAME] = tablematch
    outputs[FILTER_TABLE_FALSE_NAME] = tableunmatch

    return {
      outputs,
      query
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  _getOutputSchemas({
    workflow,
    params,
    schemas,
    connection,
    componentName
  }: GetOutputSchemasParams): TableSchemasList {
    const tablename = params.source as string
    const columns = this._getSchemaForTablename(tablename, schemas, connection)
    const fqnMatch = this._getDryRunOutputTableFqn(workflow, params, connection, componentName, FILTER_TABLE_TRUE_NAME)
    const fqnUnmatch = this._getDryRunOutputTableFqn(
      workflow,
      params,
      connection,
      componentName,
      FILTER_TABLE_FALSE_NAME
    )

    const outputMode = params.output_mode as string
    const additionalColumns = [
      {
        name: geomCol,
        type: connection.getGeoDataType()
      }
    ]

    if (outputMode === 'Full JSON Response') {
      additionalColumns.push({
        name: 'carto_geocode_metadata',
        type: ColumnDataType.String
      })
    }

    const outputColumns = addColumns(columns, additionalColumns)

    return [
      {
        fqn: fqnMatch,
        schema: outputColumns
      },
      {
        fqn: fqnUnmatch,
        schema: outputColumns
      }
    ]
  }
}

export { OracleGeocode }
